import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export const useFetchVaAttemptGroups = (page, pageSize, startDate, endDate, claimStatus = null) => {
  return useQuery(
    ['va-call-attempt-groups', { page, pageSize, startDate, endDate, claimStatus }],
    async () => {
      const res = await axios.get('/retell-admin/va-call-attempts', {
        params: { page, pageSize, startDate, endDate, claimStatus },
      });
      return res.data;
    },
    { keepPreviousData: true },
  );
};

export const usePrefetchVaAttemptGroup = () => {
  const qc = useQueryClient();
  return (groupId) =>
    qc.prefetchQuery(['va-call-attempt-group', { groupId }], async () => {
      const res = await axios.get(`/retell-admin/va-call-attempts/${groupId}`);
      return res.data;
    });
};

export const useFetchVaAttemptGroup = (groupId) => {
  return useQuery(
    ['va-call-attempt-group', { groupId }],
    async () => {
      if (!groupId) return { group: null, attempts: [] };
      const res = await axios.get(`/retell-admin/va-call-attempts/${groupId}`);
      return res.data;
    },
    { enabled: !!groupId },
  );
};

export const useFetchVaUsers = (
  page,
  pageSize,
  startDate,
  endDate,
  claimStatus = null,
  result = null,
  fromNumber = null,
  carrier = null,
  notInitialized = null,
  name = null,
) => {
  return useQuery(
    [
      'va-users',
      { page, pageSize, startDate, endDate, claimStatus, result, fromNumber, carrier, notInitialized, name },
    ],
    async () => {
      const params = { page, pageSize, startDate, endDate };
      if (claimStatus) params.claimStatus = claimStatus; // backward compat
      if (result) params.result = result;
      if (fromNumber) params.fromNumber = fromNumber;
      if (carrier) params.carrier = carrier;
      if (typeof notInitialized === 'boolean') params.notInitialized = notInitialized;
      if (name) params.name = name;
      const res = await axios.get('/retell-admin/va-users', { params });
      return res.data;
    },
    { keepPreviousData: true },
  );
};

export const useFetchVaUser = (ssn) => {
  return useQuery(
    ['va-user', { ssn }],
    async () => {
      if (!ssn) return { ssn: null, groups: [] };
      const res = await axios.get(`/retell-admin/va-users/${ssn}`);
      return res.data;
    },
    { enabled: !!ssn },
  );
};

export const useFetchVaBlocklist = () => {
  return useQuery(
    ['va-blocklist'],
    async () => {
      const res = await axios.get('/retell-admin/va-blocklist');
      return res.data;
    },
    { staleTime: 30_000 },
  );
};

export const useFetchFailedVaCalls = (
  page,
  pageSize,
  startDate,
  endDate,
  claimStatus = null,
  name = null,
) => {
  return useQuery(
    ['va-failed-calls', { page, pageSize, startDate, endDate, claimStatus, name }],
    async () => {
      const params = { page, pageSize, startDate, endDate };
      if (claimStatus) params.claimStatus = claimStatus;
      if (name) params.name = name;
      const res = await axios.get('/retell-admin/va-failed-calls', { params });
      return res.data;
    },
    { keepPreviousData: true },
  );
};

export const applyFailedQueueRating = async (claimId, newRating) => {
  const res = await axios.post(`/retell-admin/va-failed-calls/${claimId}/apply-rating`, {
    newRating,
  });
  return res.data;
};

export const retryFailedQueueClaim = async (claimId) => {
  const res = await axios.post(`/retell-admin/va-failed-calls/${claimId}/retry`);
  return res.data;
};

export const retryAllFailedQueueClaims = async (filters = {}) => {
  const res = await axios.post('/retell-admin/va-failed-calls/retry-all', filters);
  return res.data;
};

export const useFetchJobStatus = (jobId, opts = {}) => {
  return useQuery(
    ['job-status', jobId],
    async () => {
      const res = await axios.get(`/jobs/${jobId}`);
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!jobId,
      refetchInterval: (data) => {
        if (data?.jobStatus === 'pending' || data?.jobStatus === 'processing') {
          return 3000;
        }
        return false;
      },
      ...opts,
    },
  );
};
